import { sha256 } from 'js-sha256';
import { IProductDescription } from 'types/types';
import { PixelServices } from 'services/pixelServices';

type AxonUserData = {
  email?: string;
  user_id?: string;
};

type AxonItem = {
  item_id: string;
  item_name?: string;
  item_variant_id?: string;
  price?: number;
  quantity?: number;
};

type AxonBaseEventData = {
  currency?: string;
  items?: AxonItem[];
  user_data?: AxonUserData;
  value?: number;
};

type AxonPurchaseEventData = AxonBaseEventData & {
  shipping: number;
  tax: number;
  transaction_id: string;
};

const DEFAULT_ITEM_NAME = 'Senses subscription';

const normalizeEmail = (email?: string) => {
  const normalized = email?.trim().toLowerCase();
  return normalized || undefined;
};

export const buildAxonCuid = (email?: string) => {
  const normalizedEmail = normalizeEmail(email);

  return normalizedEmail ? sha256(normalizedEmail) : undefined;
};

export const buildAxonUserData = (
  email?: string,
  userId?: string,
): AxonUserData | undefined => {
  const normalizedEmail = normalizeEmail(email);

  if (!normalizedEmail && !userId) {
    return undefined;
  }

  return {
    email: normalizedEmail,
    user_id: userId || undefined,
  };
};

export const buildAxonItem = (
  productId: string,
  productData?: IProductDescription,
  value?: number,
): AxonItem[] | undefined => {
  if (!productId || !productData) {
    return undefined;
  }

  return [
    {
      item_id: productId,
      item_name: DEFAULT_ITEM_NAME,
      item_variant_id: productData.price_id || undefined,
      price: value,
      quantity: 1,
    },
  ];
};

export const buildAxonCheckoutEventData = ({
  email,
  productData,
  productId,
  userId,
  value,
}: {
  email?: string;
  productData?: IProductDescription;
  productId: string;
  userId?: string;
  value: number;
}): AxonBaseEventData | undefined => {
  if (!productData) {
    return undefined;
  }

  return {
    currency: productData.currency,
    items: buildAxonItem(productId, productData, value),
    user_data: buildAxonUserData(email, userId),
    value,
  };
};

export const buildAxonPurchaseEventData = ({
  email,
  productData,
  productId,
  transactionId,
  userId,
  value,
}: {
  email?: string;
  productData?: IProductDescription;
  productId: string;
  transactionId: string;
  userId?: string;
  value: number;
}): AxonPurchaseEventData | undefined => {
  const baseData = buildAxonCheckoutEventData({
    email,
    productData,
    productId,
    userId,
    value,
  });

  if (!baseData) {
    return undefined;
  }

  return {
    ...baseData,
    shipping: 0,
    tax: 0,
    transaction_id: transactionId,
  };
};

export const buildAxonTransactionId = ({
  prefix,
  productId,
  userId,
}: {
  prefix: string;
  productId: string;
  userId?: string;
}) => [prefix, userId || 'anonymous', productId, Date.now()].join('_');

type AxonTrackProductArgs = {
  email?: string;
  productData?: IProductDescription;
  productId: string;
  userId?: string;
};

type AxonTrackPurchaseArgs = AxonTrackProductArgs & {
  transactionId?: string;
  value?: number;
};

const AXON_PURCHASE_SESSION_PREFIX = 'axon_purchase_sent:';

const getAxonPurchaseSessionKey = ({
  email,
  productId,
  userId,
}: AxonTrackProductArgs) =>
  `${AXON_PURCHASE_SESSION_PREFIX}${userId || normalizeEmail(email) || 'anonymous'}:${productId}`;

const hasSentAxonPurchase = (key: string) => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return false;
  }

  return window.sessionStorage.getItem(key) === '1';
};

const markAxonPurchaseSent = (key: string) => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }

  window.sessionStorage.setItem(key, '1');
};

export const trackAxonViewItem = ({
  email,
  productData,
  productId,
  userId,
}: AxonTrackProductArgs) => {
  const value = (productData?.trial.amount ?? productData?.amount ?? 0) / 100;

  PixelServices.axonTrack(
    'view_item',
    buildAxonCheckoutEventData({
      email,
      productData,
      productId,
      userId,
      value,
    }),
  );
};

export const trackAxonCheckoutStart = ({
  email,
  productData,
  productId,
  userId,
}: AxonTrackProductArgs) => {
  const value = (productData?.trial.amount ?? productData?.amount ?? 0) / 100;
  const payload = buildAxonCheckoutEventData({
    email,
    productData,
    productId,
    userId,
    value,
  });

  PixelServices.axonTrack('add_to_cart', payload);
  PixelServices.axonTrack('begin_checkout', payload);
};

export const trackAxonPurchase = ({
  email,
  productData,
  productId,
  transactionId,
  userId,
  value,
}: AxonTrackPurchaseArgs) => {
  if (!productData || !productId) {
    return;
  }

  const purchaseSessionKey = getAxonPurchaseSessionKey({
    email,
    productData,
    productId,
    userId,
  });

  if (hasSentAxonPurchase(purchaseSessionKey)) {
    return;
  }

  const normalizedValue = value ?? getDefaultAxonValue(productData);

  PixelServices.axonTrack(
    'purchase',
    buildAxonPurchaseEventData({
      email,
      productData,
      productId,
      transactionId:
        transactionId ||
        buildAxonTransactionId({
          prefix: 'purchase',
          productId,
          userId,
        }),
      userId,
      value: normalizedValue,
    }),
  );

  markAxonPurchaseSent(purchaseSessionKey);
};

const getDefaultAxonValue = (productData?: IProductDescription) =>
  (productData?.trial.amount ?? productData?.amount ?? 0) / 100;
